/**
 * /contacts — paginated, filterable list. "Load more" pulls the next
 * cursor page rather than absolute pagination (keyset on
 * last_interaction_at). Bulk action bar appears when rows are selected.
 */
import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

interface ContactRow {
  id: string;
  igAccountId: string;
  igUsername: string | null;
  name: string | null;
  profilePicUrl: string | null;
  optIn: { marketing: boolean; transactional: boolean };
  lastInteractionAt: string | null;
  tagIds: string[];
}

interface TagRow {
  id: string;
  name: string;
  color: string | null;
}

interface IgAccountRow {
  id: string;
  igUsername: string;
}

const ALL = "__all__";

export const Route = createFileRoute("/_app/contacts/")({
  validateSearch: z.object({
    segmentId: z.string().uuid().optional(),
  }),
  component: ContactsListPage,
});

function ContactsListPage() {
  const { activeIgAccount } = useAuth();
  const navigate = useNavigate();
  const { segmentId } = Route.useSearch();
  const [rows, setRows] = useState<ContactRow[]>([]);
  const [tags, setTags] = useState<TagRow[]>([]);
  const [accounts, setAccounts] = useState<IgAccountRow[]>([]);
  const [segmentName, setSegmentName] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterTag, setFilterTag] = useState<string>(ALL);
  const [filterAccount, setFilterAccount] = useState<string>(() => ALL);
  const [filterOptIn, setFilterOptIn] = useState<string>(ALL);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const tagById = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);

  useEffect(() => {
    void Promise.all([loadTags(), loadAccounts()]);
  }, []);

  // Sync filterAccount to activeIgAccount whenever the active account changes.
  useEffect(() => {
    if (activeIgAccount) setFilterAccount(activeIgAccount.id);
  }, [activeIgAccount?.id]);

  useEffect(() => {
    if (!segmentId) {
      setSegmentName(null);
      return;
    }
    api
      .get<{ segment: { name: string } }>(`/segments/${segmentId}`)
      .then((r) => setSegmentName(r.data.segment.name))
      .catch(() => setSegmentName(null));
  }, [segmentId]);

  useEffect(() => {
    if (!activeIgAccount) return;
    setSelected(new Set());
    void loadFirstPage();
  }, [filterTag, filterAccount, filterOptIn, search, segmentId, activeIgAccount?.id]);

  async function loadTags() {
    try {
      const r = await api.get<{ tags: TagRow[] }>("/tags");
      setTags(r.data.tags);
    } catch {
      // Non-fatal
    }
  }
  async function loadAccounts() {
    try {
      const r = await api.get<{ accounts: IgAccountRow[] }>("/ig/accounts");
      setAccounts(r.data.accounts);
    } catch {
      // Non-fatal
    }
  }

  async function loadFirstPage() {
    setLoading(true);
    setError(null);
    setRows([]);
    try {
      const r = await fetchPage(undefined);
      setRows(normalizeRows(r.contacts));
      setCursor(r.nextCursor);
      setHasMore(Boolean(r.nextCursor));
    } catch {
      setError("Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }

  async function loadMore() {
    if (!cursor) return;
    try {
      const r = await fetchPage(cursor);
      setRows((cur) => [...cur, ...normalizeRows(r.contacts)]);
      setCursor(r.nextCursor);
      setHasMore(Boolean(r.nextCursor));
    } catch {
      setError("Failed to load more");
    }
  }

  async function fetchPage(
    nextCursor: string | undefined,
  ): Promise<{ contacts: ContactRow[]; nextCursor: string | null }> {
    if (segmentId) {
      const params: Record<string, string> = { limit: "50" };
      if (nextCursor) params.cursor = nextCursor;
      const r = await api.get<{ contacts: ContactRow[]; nextCursor: string | null }>(
        `/segments/${segmentId}/contacts`,
        { params },
      );
      return r.data;
    }
    const params = buildParams();
    if (nextCursor) params.cursor = nextCursor;
    const r = await api.get<{ contacts: ContactRow[]; nextCursor: string | null }>(
      "/contacts",
      { params },
    );
    return r.data;
  }

  function normalizeRows(rs: ContactRow[]): ContactRow[] {
    // /segments/:id/contacts doesn't return tagIds (the SQL query is
    // segment-compiled, no per-row tag join). Backfill so the table
    // renders cleanly without an N+1.
    return rs.map((r) => ({ ...r, tagIds: r.tagIds ?? [] }));
  }

  function clearSegment() {
    void navigate({ to: "/contacts", search: {} });
  }

  function buildParams(): Record<string, string> {
    const p: Record<string, string> = { limit: "50" };
    if (filterTag !== ALL) p.tagId = filterTag;
    if (filterAccount !== ALL) p.igAccountId = filterAccount;
    if (filterOptIn !== ALL) p.optInMarketing = filterOptIn;
    if (search.trim()) p.q = search.trim();
    return p;
  }

  function toggleRow(id: string) {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selected.size === rows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(rows.map((r) => r.id)));
    }
  }

  async function runBulk(action: "tag" | "untag" | "opt_out_marketing", tagId?: string) {
    if (selected.size === 0) return;
    if ((action === "tag" || action === "untag") && !tagId) return;
    setBulkBusy(true);
    try {
      const r = await api.post<{ affected: number }>("/contacts/bulk", {
        contactIds: [...selected],
        action,
        tagId,
      });
      // eslint-disable-next-line no-alert
      alert(`Bulk ${action}: ${r.data.affected} contact(s) updated.`);
      setSelected(new Set());
      void loadFirstPage();
    } catch {
      setError("Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  }

  function exportCsv() {
    const params = new URLSearchParams(buildParams());
    const url = `${api.defaults.baseURL}/contacts/export/csv?${params.toString()}`;
    window.open(url, "_blank");
  }

  return (
    <section style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 28 }}>Contacts</h1>
        <button onClick={exportCsv} className="brut-button is-secondary" style={{ fontSize: 13 }}>
          Export CSV
        </button>
      </div>

      {segmentId ? (
        <div
          className="brut-card"
          style={{
            padding: 10,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "var(--accent)",
          }}
        >
          <strong>Segment:</strong>
          <span>{segmentName ?? "…"}</span>
          <Link
            to="/segments/$segmentId"
            params={{ segmentId }}
            className="brut-button is-secondary"
            style={{ marginLeft: "auto", fontSize: 13, padding: "4px 10px" }}
          >
            Edit
          </Link>
          <button
            onClick={clearSegment}
            className="brut-button is-secondary"
            style={{ fontSize: 13, padding: "4px 10px" }}
          >
            Clear
          </button>
        </div>
      ) : null}

      <div
        className="brut-card"
        style={{
          marginBottom: 16,
          padding: 12,
          display: "grid",
          gap: 10,
          gridTemplateColumns: "1fr 1fr 1fr 1fr",
          opacity: segmentId ? 0.55 : 1,
          pointerEvents: segmentId ? "none" : undefined,
        }}
      >
        <div>
          <label className="brut-label">Search</label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="name or @handle"
            className="brut-input"
          />
        </div>
        <div>
          <label className="brut-label">Tag</label>
          <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} className="brut-input">
            <option value={ALL}>All tags</option>
            {tags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="brut-label">Account</label>
          <select value={filterAccount} onChange={(e) => setFilterAccount(e.target.value)} className="brut-input">
            <option value={ALL}>All accounts</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                @{a.igUsername}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="brut-label">Marketing opt-in</label>
          <select value={filterOptIn} onChange={(e) => setFilterOptIn(e.target.value)} className="brut-input">
            <option value={ALL}>Any</option>
            <option value="true">Opted in</option>
            <option value="false">Opted out</option>
          </select>
        </div>
      </div>

      {selected.size > 0 ? (
        <div
          className="brut-card"
          style={{
            padding: 10,
            marginBottom: 12,
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "var(--accent)",
          }}
        >
          <strong>{selected.size}</strong> selected
          <BulkTagPicker tags={tags} onPick={(tid) => void runBulk("tag", tid)} disabled={bulkBusy} label="Add tag" />
          <BulkTagPicker tags={tags} onPick={(tid) => void runBulk("untag", tid)} disabled={bulkBusy} label="Remove tag" />
          <button onClick={() => void runBulk("opt_out_marketing")} className="brut-button is-secondary" disabled={bulkBusy} style={{ fontSize: 13, padding: "4px 10px" }}>
            Opt out (marketing)
          </button>
          <button onClick={() => setSelected(new Set())} className="brut-button is-secondary" style={{ fontSize: 13, padding: "4px 10px", marginLeft: "auto" }}>
            Clear
          </button>
        </div>
      ) : null}

      {error ? (
        <div className="brut-card" style={{ background: "#ffd7d7", padding: 10, marginBottom: 12 }}>
          {error}
        </div>
      ) : null}

      {loading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <div className="brut-card">No contacts match.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: "3px solid var(--ink)" }}>
              <th style={{ width: 28, padding: 8 }}>
                <input
                  type="checkbox"
                  checked={selected.size === rows.length && rows.length > 0}
                  onChange={toggleAll}
                />
              </th>
              <th style={{ textAlign: "left", padding: 8 }}>Contact</th>
              <th style={{ textAlign: "left", padding: 8 }}>Tags</th>
              <th style={{ textAlign: "left", padding: 8 }}>Last seen</th>
              <th style={{ textAlign: "left", padding: 8 }}>Marketing</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: 8 }}>
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleRow(c.id)}
                  />
                </td>
                <td style={{ padding: 8 }}>
                  <Link
                    to="/contacts/$contactId"
                    params={{ contactId: c.id }}
                    style={{ fontWeight: 700 }}
                  >
                    {c.name ?? c.igUsername ?? "—"}
                  </Link>
                  {c.igUsername && c.name ? (
                    <span style={{ color: "var(--muted)", marginLeft: 6 }}>@{c.igUsername}</span>
                  ) : null}
                </td>
                <td style={{ padding: 8 }}>
                  {c.tagIds.length === 0 ? (
                    <span style={{ color: "var(--muted)", fontSize: 12 }}>—</span>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {c.tagIds.map((tid) => {
                        const t = tagById.get(tid);
                        if (!t) return null;
                        return (
                          <span
                            key={tid}
                            style={{
                              background: t.color ?? "#ddd",
                              border: "2px solid var(--ink)",
                              padding: "1px 6px",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            {t.name}
                          </span>
                        );
                      })}
                    </div>
                  )}
                </td>
                <td style={{ padding: 8, fontSize: 12, color: "var(--muted)" }}>
                  {c.lastInteractionAt ? formatTime(c.lastInteractionAt) : "—"}
                </td>
                <td style={{ padding: 8, fontSize: 12 }}>
                  {c.optIn.marketing ? "✓" : "✗"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {hasMore ? (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button onClick={() => void loadMore()} className="brut-button is-secondary">
            Load more
          </button>
        </div>
      ) : null}
    </section>
  );
}

function BulkTagPicker({ tags, onPick, disabled, label }: { tags: TagRow[]; onPick: (id: string) => void; disabled: boolean; label: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={disabled || tags.length === 0}
        className="brut-button is-secondary"
        style={{ fontSize: 13, padding: "4px 10px" }}
      >
        {label} ▾
      </button>
      {open ? (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            background: "var(--paper)",
            border: "3px solid var(--ink)",
            boxShadow: "4px 4px 0 var(--ink)",
            minWidth: 160,
            zIndex: 5,
            maxHeight: 240,
            overflow: "auto",
          }}
        >
          {tags.map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setOpen(false);
                onPick(t.id);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "6px 10px",
                fontSize: 13,
                background: "transparent",
                border: 0,
                borderBottom: "1px solid #eee",
                cursor: "pointer",
              }}
            >
              {t.name}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function formatTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}
